import {
    coverageNote,
    formatProviderMoney,
    formatProviderTotals,
    providerLabel,
} from '@apps/web/components/admin/llm_spend/provider_spend_math';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { type LlmProviderBillingSection } from '@libraries/typescript/api_client';

export function ProviderSpendCard({
    section,
    range,
}: {
    readonly section: LlmProviderBillingSection | null;
    readonly range: {
        readonly range_start: string;
        readonly range_end: string;
    };
}) {
    if (section === null || section.providers.length === 0) {
        return null;
    }
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">
                    Provider-reported spend
                </CardTitle>
                <CardDescription className="text-xs">
                    {section.scope_note}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Provider</TableHead>
                            <TableHead>
                                Project, workspace, or service
                            </TableHead>
                            <TableHead className="text-right">
                                Billed total
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {section.providers.map((provider) => {
                            const note = coverageNote(provider, range);
                            const status =
                                note === null
                                    ? 'Not imported'
                                    : 'No spend reported';
                            const hasTotals = provider.totals.length > 0;
                            return (
                                <TableRow key={provider.provider}>
                                    <TableCell className="font-medium">
                                        {providerLabel(provider.provider)}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground text-xs">
                                        {hasTotals
                                            ? provider.projects.map(
                                                  (project) => (
                                                      <div
                                                          key={`${project.label}-${project.currency}`}
                                                          className="tabular-nums"
                                                      >
                                                          {project.label} ·{' '}
                                                          {formatProviderMoney(
                                                              project.amount,
                                                              project.currency,
                                                          )}
                                                      </div>
                                                  ),
                                              )
                                            : status}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {hasTotals
                                            ? formatProviderTotals(
                                                  provider.totals,
                                              )
                                            : status}
                                        {note !== null && (
                                            <div className="text-muted-foreground text-xs">
                                                {note}
                                            </div>
                                        )}
                                        {provider.has_preliminary && (
                                            <div className="text-muted-foreground text-xs">
                                                Includes preliminary costs
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
